import { useSyncExternalStore } from "react";

// Pinned/favorite teams, stored client-side. There's no per-user backend (the API has no per-user
// data yet), so this lives in localStorage — per browser/device, not synced across devices/logins.
// A module-level store with useSyncExternalStore keeps every TeamRow and the list itself in sync
// when a star is toggled (the native `storage` event doesn't fire in the tab that made the change).

const STORAGE_KEY = "gcstats:favorite-teams";

function read(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === "string")) : new Set();
  } catch {
    return new Set();
  }
}

let favorites = read();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function toggleFavoriteTeam(id: string): void {
  const next = new Set(favorites); // new reference so useSyncExternalStore sees a change
  if (next.has(id)) next.delete(id);
  else next.add(id);
  favorites = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(favorites)));
  } catch {
    /* storage full / unavailable — keep the in-memory state so the UI still responds */
  }
  emit();
}

// Keep other tabs in sync when one of them changes the favorites.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      favorites = read();
      emit();
    }
  });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Set<string> {
  return favorites;
}

export function useFavoriteTeams() {
  const favs = useSyncExternalStore(subscribe, getSnapshot);
  return {
    favorites: favs,
    isFavorite: (id: string) => favs.has(id),
    toggle: toggleFavoriteTeam,
  };
}
